// Le temps du jeu : 1 an = 15 minutes réelles (une heure au début, accéléré 4 fois après les essais).

export const REAL_SECONDS_PER_YEAR = 900;
export const DAYS_PER_YEAR = 365;
export const REAL_SECONDS_PER_DAY = REAL_SECONDS_PER_YEAR / DAYS_PER_YEAR;

/** Le prologue, jusqu'au premier emploi, passe 2 fois plus vite que la vie active : 1 an en 7 min 30 s. */
export const PROLOGUE_SPEED = 2;
/** L'avance rapide, qui consomme la réserve de temps. */
export const FAST_FORWARD_SPEED = 4;
/** Au retour, jusqu'à un an est calculé automatiquement. */
export const AUTO_OFFLINE_CAP_DAYS = DAYS_PER_YEAR;
/** Au-delà, l'absence va dans une réserve plafonnée à cinq ans. */
export const RESERVE_CAP_DAYS = 5 * DAYS_PER_YEAR;
/** Un écart de plus d'une minute entre deux battements compte comme une absence. */
export const OFFLINE_THRESHOLD_MS = 60_000;

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

export function todayISO(now: number): string {
  return toISO(new Date(now));
}

function parseISO(iso: string): Date {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function toISO(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Vérifie une date au format AAAA-MM-JJ (celui des champs de date du navigateur). */
export function isValidISODate(iso: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return false;
  const date = parseISO(iso);
  return toISO(date) === iso;
}

/** Ajoute des années à une date ; un 29 février devient le 1er mars les années non bissextiles. */
export function addYearsISO(iso: string, years: number): string {
  const date = parseISO(iso);
  return toISO(new Date(date.getFullYear() + years, date.getMonth(), date.getDate()));
}

/**
 * Le premier jour de la vie de jeu : l'été des 18 ans, pour que le job d'été tombe en été et les études
 * à la rentrée. Le jour même des 18 ans s'il tombe en juillet ou en août, sinon le 1er juillet qui suit.
 */
export function startOfLife(birthISO: string): string {
  const birthday = addYearsISO(birthISO, 18);
  const year = Number(birthday.slice(0, 4));
  const month = Number(birthday.slice(5, 7));
  if (month === 7 || month === 8) return birthday;
  return `${month < 7 ? year : year + 1}-07-01`;
}

/** Nombre d'années pleines entre une date de naissance et une date. */
export function fullYearsBetween(fromISO: string, to: Date): number {
  const from = parseISO(fromISO);
  let years = to.getFullYear() - from.getFullYear();
  const beforeBirthday =
    to.getMonth() < from.getMonth() || (to.getMonth() === from.getMonth() && to.getDate() < from.getDate());
  if (beforeBirthday) years -= 1;
  return years;
}

/** Âge du personnage au jour de jeu donné, d'après sa date de naissance. */
export function ageOnDay(birthISO: string, startISO: string, day: number): number {
  return fullYearsBetween(birthISO, dateAtDay(startISO, day));
}

/** Date du calendrier après un nombre de jours de jeu. */
export function dateAtDay(startISO: string, day: number): Date {
  const start = parseISO(startISO);
  return new Date(start.getFullYear(), start.getMonth(), start.getDate() + Math.floor(day));
}

/** Nombre de changements de mois depuis le début de la partie. */
export function monthIndexAt(startISO: string, day: number): number {
  const start = parseISO(startISO);
  const date = dateAtDay(startISO, day);
  return (date.getFullYear() - start.getFullYear()) * 12 + (date.getMonth() - start.getMonth());
}

/** Heure du jour de jeu, de 0 à 23 : un jour de jeu dure environ 2,5 secondes réelles. */
export function hourOfDay(day: number): number {
  return Math.min(23, Math.floor((day - Math.floor(day)) * 24));
}

/** Jours de jeu restants avant le premier du mois suivant, quand tombent salaire et factures. */
export function daysUntilNextMonth(startISO: string, day: number): number {
  const today = dateAtDay(startISO, day);
  const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  const wholeDays = Math.round((nextMonth.getTime() - today.getTime()) / 86_400_000);
  return Math.max(0, wholeDays - (day - Math.floor(day)));
}

export function ageAt(day: number): number {
  return 18 + Math.floor(day / DAYS_PER_YEAR);
}
